import { TextField } from '~/common/components';
import { usePurchaseContext } from '~/purchase/context';
import { Trans, useI18n } from '~/common/context';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Card,
  Col,
  Flex,
  FormContainer,
  Row,
  Text,
  Title,
  Alert,
} from '@wyz/ui';
import { useForm } from 'react-hook-form';
import { CartRow, CartTotalSection } from '~/purchase/components';
import { MIN_CART_QUANTITY } from '~/purchase/helpers';
import { getCartQuantitySchema } from '~/purchase/schemas';
import {
  ICart,
  ICartMeta,
  ICartValidationErrorResponse,
} from '~/purchase/types';
import { useEffect, useEffectEvent, useMemo } from 'react';
import { isNil } from 'lodash-es';
import { CUSTOMER_CONTACT_TYPE, IUserCustomer } from '~/common/types';
import { CUSTOMER_LEVEL } from '@wyz/types';
import { useAccessControl } from '~/common/hooks';
import { PurchaseErrorCodes, ROLES } from '~/common/helpers';

type GroupedItems = {
  items: ICart['items'];
  replaced: Array<ICart['items']>;
};

function groupItems(
  cart: ICart,
  validationErrors: ICartValidationErrorResponse['errors'],
): GroupedItems {
  const groupByItemId: Record<string, string> = validationErrors.reduce(
    (groups, error) =>
      !isNil(error.errors.ERR_OFFER_REPLACED)
        ? {
            ...groups,
            [error.item.id]: error.errors.ERR_OFFER_REPLACED,
          }
        : groups,
    {},
  );

  const result: GroupedItems = { items: [], replaced: [] };
  const groups: Record<string, ICart['items']> = {};
  for (const item of cart.items) {
    if (!isNil(groupByItemId[item.id])) {
      const groupId = groupByItemId[item.id];
      // item is a replacement (split / merge) add it to its corresponding group (value of the error)
      if (!groups[groupId]) {
        groups[groupId] = [];
      }
      groups[groupId].push(item);
    } else {
      result.items.push(item);
    }
  }

  result.replaced = Object.values(groups);
  return result;
}

type Props = {
  currentCart: ICart;
  privileges: ICartMeta;
  validationErrors: ICartValidationErrorResponse['errors'];
  onRemoveItem: (cartItemId: string) => void;
  onSubmit: (comment?: string) => void;
  isSubmitting: boolean;
  customer: IUserCustomer;
};

function CartForm({
  currentCart,
  privileges,
  validationErrors,
  onRemoveItem,
  onSubmit,
  isSubmitting,
  customer,
}: Props) {
  const { t } = useI18n();
  const { state } = usePurchaseContext();
  const acl = useAccessControl();

  const quantitySchema = getCartQuantitySchema(
    currentCart.items.reduce(
      (quantities, item) => ({
        ...quantities,
        [item.id]: item.supplier.stock,
      }),
      {},
    ) || {},
    MIN_CART_QUANTITY,
  );

  const methods = useForm({
    resolver: zodResolver(quantitySchema),
    mode: 'all',
    reValidateMode: 'onChange',
  });

  const updateQuantity = useEffectEvent((quantity: Record<string, number>) => {
    methods.setValue('quantity', quantity);
  });
  useEffect(() => {
    updateQuantity(
      currentCart.items.reduce(
        (quantities, item) => ({
          ...quantities,
          [item.id]: item.quantity,
        }),
        {},
      ),
    );
  }, [currentCart]);

  const groupedItems = useMemo(
    () => groupItems(currentCart, validationErrors),
    [currentCart, validationErrors],
  );

  return (
    <FormContainer
      methods={methods}
      submitHandler={(formData) => {
        onSubmit(formData.comment ?? undefined);
      }}
    >
      <Row>
        <Col xs='8'>
          <Flex direction='column' className='gap-3'>
            {groupedItems.items.map((item) => (
              <CartRow
                key={item.id}
                cartItem={item}
                privileges={privileges}
                onRemoveItem={onRemoveItem}
                customer={customer}
                errors={
                  validationErrors.find(
                    (errorItem) => errorItem.item.id === item.id,
                  )?.errors
                }
              />
            ))}
            {groupedItems.replaced.map((group, index) => (
              <Flex
                direction='column'
                className='border border-warning-text border-2 rounded gap-3'
                key={`${group[0]?.tyre.ean}-${group[0]?.supplier.id}-${index}`}
              >
                {group.map((item) => (
                  <CartRow
                    key={item.id}
                    cartItem={item}
                    privileges={privileges}
                    onRemoveItem={onRemoveItem}
                    customer={customer}
                    errors={
                      validationErrors.find(
                        (errorItem) => errorItem.item.id === item.id,
                      )?.errors
                    }
                  />
                ))}
              </Flex>
            ))}
            {validationErrors.some(
              ({ errors }) =>
                errors[PurchaseErrorCodes.ERR_GET_STOCK_PRICE_SERVER_ERROR] ||
                errors[PurchaseErrorCodes.ERR_GET_STOCK_PRICE_TIMEOUT],
            ) && (
              <Alert variant='warning' className='mt-4'>
                <Trans i18nKey='app.purchase.cart_page.dms_error' />
              </Alert>
            )}
            <Card className='p-4'>
              <Title heading='lg' weight='bold' className='mb-2'>
                <Trans i18nKey='app.purchase.orders.table.comment' />
              </Title>
              <TextField
                type='text'
                name='comment'
                as='textarea'
                placeholder={t('app.purchase.cart_page.comment')}
              />
            </Card>
          </Flex>
          {acl.isGranted([ROLES.ROLE_VIEW_LOGISTIC_CONTACT_WARNING]) &&
            state.targetCustomer?.level === CUSTOMER_LEVEL.N1 &&
            !(state.targetCustomer.contacts ?? []).some((contact) =>
              contact.types.includes(CUSTOMER_CONTACT_TYPE.LOGISTICS_CONTACT),
            ) && (
              <Alert variant='warning' className='mt-3'>
                <Text color='warning' heading='normal'>
                  <Trans
                    i18nKey='app.purchase.cart_page.logistics_contact'
                    components={[<strong key={0} />]}
                  />
                </Text>
              </Alert>
            )}
        </Col>
        <Col xs='4'>
          <CartTotalSection
            customer={customer}
            values={currentCart}
            privileges={privileges}
            mode='CART'
            isSubmitting={isSubmitting}
            valuesChanged={Boolean(validationErrors.length)}
            currency={currentCart.items.at(0)?.price.currency as string}
            canSubmitOrder={
              false ===
              validationErrors.some(
                ({ errors }) =>
                  false !==
                    errors[
                      PurchaseErrorCodes.ERR_GET_STOCK_PRICE_SERVER_ERROR
                    ] ||
                  false !==
                    errors[PurchaseErrorCodes.ERR_GET_STOCK_PRICE_TIMEOUT],
              )
            }
          />
        </Col>
      </Row>
    </FormContainer>
  );
}

export default CartForm;
